import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './TaskManager.css';

const TaskManager = () => {
  const { isAdmin, token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedQuadrant, setSelectedQuadrant] = useState(1);
  const [newTask, setNewTask] = useState({ title: '', description: '' });
  const [editingTask, setEditingTask] = useState(null);

  // 象限配置
  const quadrants = {
    1: { title: '重要且紧急', color: '#ff4757', description: '立即处理' },
    2: { title: '重要但不紧急', color: '#ffa502', description: '计划处理' },
    3: { title: '不重要但紧急', color: '#3742fa', description: '委托处理' },
    4: { title: '不重要且不紧急', color: '#2ed573', description: '消除处理' }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchTasks();
    }
  }, [isAdmin]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/tasks', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      } else {
        setError('获取任务失败');
      }
    } catch (err) {
      setError('网络错误');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    if (!newTask.title.trim()) {
      setError('任务标题不能为空');
      return;
    }

    try {
      const response = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newTask.title,
          description: newTask.description,
          quadrant: selectedQuadrant
        })
      });

      if (response.ok) {
        const newTaskData = await response.json();
        setTasks(prev => [...prev, newTaskData]);
        setNewTask({ title: '', description: '' });
        setShowAddModal(false);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.error || '创建任务失败');
      }
    } catch (err) {
      setError('网络错误');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(prev => prev.map(task => 
          task.id === taskId ? updatedTask : task
        ));
      } else {
        setError('更新任务状态失败');
      }
    } catch (err) {
      setError('网络错误');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('确定要删除这个任务吗？')) {
      return;
    }

    try {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setTasks(prev => prev.filter(task => task.id !== taskId));
      } else {
        setError('删除任务失败');
      }
    } catch (err) {
      setError('网络错误');
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const response = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(prev => prev.map(task => 
          task.id === taskId ? updatedTask : task
        ));
        setEditingTask(null);
      } else {
        setError('更新任务失败');
      }
    } catch (err) {
      setError('网络错误');
    }
  };

  const getTasksByQuadrant = (quadrant) => {
    return tasks.filter(task => task.quadrant === quadrant);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="task-manager">
        <div className="access-denied">
          <h2>访问受限</h2>
          <p>您需要管理员权限才能访问任务管理功能</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="task-manager">
        <div className="loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="task-manager">
      <div className="task-header">
        <h1>任务四象限管理</h1>
        <button 
          className="add-task-btn"
          onClick={() => setShowAddModal(true)}
        >
          + 添加任务
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="quadrants-grid">
        {Object.entries(quadrants).map(([quadrant, config]) => {
          const quadrantTasks = getTasksByQuadrant(parseInt(quadrant));
          
          return (
            <div key={quadrant} className="quadrant" style={{ borderColor: config.color }}>
              <div className="quadrant-header" style={{ backgroundColor: config.color }}>
                <h3>{config.title}</h3>
                <span className="quadrant-description">{config.description}</span>
                <span className="task-count">({quadrantTasks.length})</span>
              </div>
              
              <div className="quadrant-content">
                {quadrantTasks.map(task => (
                  <div 
                    key={task.id} 
                    className={`task-item ${task.status === 'completed' ? 'completed' : ''}`}
                  >
                    {editingTask === task.id ? (
                      <div className="task-edit">
                        <input
                          type="text"
                          value={task.title}
                          onChange={(e) => setTasks(prev => prev.map(t => 
                            t.id === task.id ? { ...t, title: e.target.value } : t
                          ))}
                          className="edit-title"
                        />
                        <textarea
                          value={task.description || ''}
                          onChange={(e) => setTasks(prev => prev.map(t => 
                            t.id === task.id ? { ...t, description: e.target.value } : t
                          ))}
                          className="edit-description"
                          placeholder="描述..."
                        />
                        <div className="edit-actions">
                          <button 
                            onClick={() => updateTask(task.id, { 
                              title: task.title, 
                              description: task.description 
                            })}
                            className="save-btn"
                          >
                            保存
                          </button>
                          <button 
                            onClick={() => setEditingTask(null)}
                            className="cancel-btn"
                          >
                            取消
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="task-content">
                          <h4 className="task-title">{task.title}</h4>
                          {task.description && (
                            <p className="task-description">{task.description}</p>
                          )}
                          <div className="task-meta">
                            <span className="task-date">{formatDate(task.created_at)}</span>
                            {task.status === 'completed' && task.completed_at && (
                              <span className="completed-date">
                                完成于 {formatDate(task.completed_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="task-actions">
                          <button
                            onClick={() => setEditingTask(task.id)}
                            className="edit-btn"
                            title="编辑"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => updateTaskStatus(
                              task.id, 
                              task.status === 'completed' ? 'pending' : 'completed'
                            )}
                            className={`status-btn ${task.status === 'completed' ? 'undo' : 'complete'}`}
                            title={task.status === 'completed' ? '标记为未完成' : '标记为完成'}
                          >
                            {task.status === 'completed' ? '↶' : '✓'}
                          </button>
                          <button
                            onClick={() => deleteTask(task.id)}
                            className="delete-btn"
                            title="删除"
                          >
                            🗑️
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                
                {quadrantTasks.length === 0 && (
                  <div className="empty-quadrant">
                    <p>暂无任务</p>
                    <button 
                      className="add-here-btn"
                      onClick={() => {
                        setSelectedQuadrant(parseInt(quadrant));
                        setShowAddModal(true);
                      }}
                    >
                      在此添加任务
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 添加任务模态框 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>添加新任务</h3>
              <button 
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>象限选择</label>
                <select 
                  value={selectedQuadrant}
                  onChange={(e) => setSelectedQuadrant(parseInt(e.target.value))}
                >
                  {Object.entries(quadrants).map(([quadrant, config]) => (
                    <option key={quadrant} value={quadrant}>
                      {config.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>任务标题 *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="输入任务标题..."
                />
              </div>
              
              <div className="form-group">
                <label>任务描述</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="输入任务描述..."
                  rows="3"
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setShowAddModal(false)}
              >
                取消
              </button>
              <button 
                className="confirm-btn"
                onClick={createTask}
              >
                创建任务
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManager;